import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { DEFAULT_TAGS, statusOptionsFor, colorOf } from "@/lib/constants";
import { planPresetsFor, moodEmoji, sleepEmoji, getPlanUsage, bumpPlanUsage, getDismissedPlans, dismissPlan, restorePlans } from "@/lib/vibe";
import { api, auth } from "@/lib/api";
import { useSuspendPollingWhileOpen } from "@/lib/dialogPolling";
import { encryptNote, decryptNote, isEncryptedNote, hasPrivateNoteKey } from "@/lib/privateCrypto";
import { getHouseUnlockPin } from "@/lib/houseLock";
import { toast } from "sonner";
import { X, EyeOff, Eye, ChevronDown, ChevronUp } from "lucide-react";

export default function CheckinDialog({ member, existing, onClose, onSaved, isSelf, settings }) {
  useSuspendPollingWhileOpen();
  const navigate = useNavigate();
  const [sleep, setSleep] = useState(existing?.sleep ?? 7);
  const [mood, setMood] = useState(existing?.mood ?? 7);
  // Today's plan: multiple presets (array) + optional free-text custom
  const [plans, setPlans] = useState(() => {
    if (!existing?.today_plan) return [];
    return existing.today_plan.split(" · ").map((s) => s.trim()).filter(Boolean);
  });
  const [customPlan, setCustomPlan] = useState("");
  const [tags, setTags] = useState(existing?.tags ?? []);
  const [status, setStatus] = useState(existing?.status ?? "home");
  const [note, setNote] = useState(existing?.note ?? "");

  // Decrypt the existing note when the dialog opens. Legacy plaintext passes
  // through untouched; ciphertext becomes readable here (only on a device
  // that has the derived key, i.e. joined via PIN).
  useEffect(() => {
    const raw = existing?.note;
    if (!raw || !isEncryptedNote(raw)) return;
    let cancelled = false;
    decryptNote(raw, auth.getFamilyId()).then((pt) => {
      if (!cancelled) setNote(pt?.startsWith("🔒") ? "" : pt || "");
    });
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [existing?.id]);
  const [customLocation, setCustomLocation] = useState(
    existing?.custom_location ?? ""
  );
  const [notePrivate, setNotePrivate] = useState(!!existing?.note_private);
  const [showMore, setShowMore] = useState(false);
  const [showAllPlans, setShowAllPlans] = useState(false);
  const [saving, setSaving] = useState(false);
  const sleepRef = React.useRef(null);

  // Plan preset ordering: most-used first. Presets with count>=2 stay primary; the rest collapse.
  const planUsage = React.useMemo(() => getPlanUsage(), []);
  const [editIdeas, setEditIdeas] = useState(false);
  const [dismissed, setDismissed] = useState(() => new Set(getDismissedPlans()));
  const availablePresets = React.useMemo(
    () =>
      planPresetsFor(
        member.role,
        settings?.custom_plans_adult || [],
        settings?.custom_plans_kid || []
      ).filter((p) => !dismissed.has(p) || plans.includes(p) || editIdeas),
    [member.role, settings, dismissed, plans, editIdeas]
  );
  const hasDismissed = dismissed.size > 0;
  const doDismiss = (p) => {
    dismissPlan(p);
    setDismissed((s) => {
      const next = new Set(s);
      next.add(p);
      return next;
    });
  };
  const doRestore = () => {
    restorePlans();
    setDismissed(new Set());
  };
  const availableStatus = React.useMemo(
    () => statusOptionsFor(member.role),
    [member.role]
  );
  const { primaryPresets, morePresets } = React.useMemo(() => {
    const sorted = [...availablePresets].sort(
      (a, b) => (planUsage[b] || 0) - (planUsage[a] || 0)
    );
    const primary = sorted.filter((p) => (planUsage[p] || 0) >= 2);
    // Ensure at least 3 primary presets visible on first use
    while (primary.length < 3 && sorted.length > primary.length) {
      const next = sorted[primary.length];
      if (!primary.includes(next)) primary.push(next);
    }
    const more = sorted.filter((p) => !primary.includes(p));
    return { primaryPresets: primary, morePresets: more };
  }, [planUsage, availablePresets]);

  useEffect(() => {
    const onKey = (e) => e.key === "Escape" && onClose();
    window.addEventListener("keydown", onKey);
    // Auto-focus the sleep slider for quick logging
    const t = setTimeout(() => sleepRef.current?.focus(), 100);
    return () => {
      window.removeEventListener("keydown", onKey);
      clearTimeout(t);
    };
  }, [onClose]);

  const toggleTag = (key) =>
    setTags((t) => (t.includes(key) ? t.filter((x) => x !== key) : [...t, key]));

  const togglePlan = (p) =>
    setPlans((arr) => (arr.includes(p) ? arr.filter((x) => x !== p) : [...arr, p]));

  const addCustomPlan = () => {
    const v = customPlan.trim();
    if (!v) return;
    setPlans((arr) => (arr.includes(v) ? arr : [...arr, v]));
    setCustomPlan("");
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const planText = plans.length ? plans.join(" · ") : null;
      const trimmedNote = note.trim() || null;
      // Encrypt the note client-side when the "private" toggle is on. The
      // server stores only ciphertext — it physically cannot read this.
      let noteToSend = trimmedNote;
      if (trimmedNote && notePrivate) {
        noteToSend = await encryptNote(trimmedNote, auth.getFamilyId());
      }
      const updated = await api.upsertCheckin({
        member_id: member.id,
        sleep: Number(sleep),
        mood: Number(mood),
        tags,
        status,
        note: noteToSend,
        custom_location: customLocation.trim() || null,
        note_private: notePrivate,
        today_plan: planText,
        // House-lock: if the member lives in a locked house that was
        // unlocked on this device, carry the PIN with the write.
        house_pin: member.house_id ? getHouseUnlockPin(member.house_id) : null,
      });
      plans.forEach((p) => {
        if (availablePresets.includes(p)) bumpPlanUsage(p);
      });
      toast.success("Saved");
      onSaved(updated);
    } catch (e) {
      toast.error("Couldn't save. Try again.");
    } finally {
      setSaving(false);
    }
  };

  const color = colorOf(member.color);

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-3 md:p-6"
      style={{ background: "rgba(20,20,18,0.45)", backdropFilter: "blur(6px)" }}
      onClick={onClose}
      data-testid="checkin-dialog"
    >
      <div
        className="bg-[color:var(--surface)] rounded-xl w-full max-w-md max-h-[94svh] overflow-y-auto shadow-2xl border border-[color:var(--line)]"
        style={{ overflowAnchor: "none" }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="sticky top-0 z-10 bg-[color:var(--surface)] px-5 pt-5 pb-4 flex items-center gap-3 border-b border-[color:var(--line-soft)]">
          <div
            className="w-10 h-10 rounded-full flex items-center justify-center text-xl flex-shrink-0"
            style={{ background: color.hex }}
          >
            {member.avatar_emoji}
          </div>
          <div className="flex-1">
            <div className="label-tiny">{isSelf ? "Your check-in" : "Checking in"}</div>
            <h2 className="font-display text-xl">{member.name}</h2>
          </div>
          {member.role !== "pet" && (
            <button
              type="button"
              onClick={() => {
                onClose();
                navigate(`/history/${member.id}`);
              }}
              className="ff-chip text-[12px] shrink-0 !bg-[#F6D6DA] !border-[#E6B4BA] !text-[#7A2E3A] hover:!bg-[#F1BFC6]"
              data-testid="checkin-view-history"
              title={`View ${member.name}'s mood and sleep history`}
            >
              History →
            </button>
          )}
          <button
            onClick={onClose}
            className="ff-btn h-9 w-9 !p-0"
            data-testid="checkin-close"
            aria-label="Close"
          >
            <X className="w-4 h-4" strokeWidth={2} />
          </button>
        </div>

        <div className="p-5 space-y-7">
          {/* Mood first — easier to re-log later */}
          <div>
            <div className="flex items-center justify-between mb-3">
              <div className="label-tiny">Mood</div>
              <div className="font-display text-3xl tabular leading-none flex items-baseline gap-2">
                {mood}
                <span className="text-base text-[color:var(--ink-3)]">/10</span>
                <span className="text-2xl ml-1">{moodEmoji(Number(mood))}</span>
              </div>
            </div>
            <input
              ref={sleepRef}
              type="range"
              min="1"
              max="10"
              value={mood}
              onChange={(e) => setMood(e.target.value)}
              className="ff-slider"
              data-testid="mood-slider"
            />
          </div>

          {/* Sleep */}
          <div>
            <div className="flex items-center justify-between mb-3">
              <div className="label-tiny">Sleep</div>
              <div className="font-display text-3xl tabular leading-none flex items-baseline gap-2">
                {sleep}
                <span className="text-base text-[color:var(--ink-3)]">/10</span>
                <span className="text-2xl ml-1">{sleepEmoji(Number(sleep))}</span>
              </div>
            </div>
            <input
              type="range"
              min="1"
              max="10"
              value={sleep}
              onChange={(e) => setSleep(e.target.value)}
              className="ff-slider"
              data-testid="sleep-slider"
            />
          </div>

          {/* Feelings — primary, above Today's plan */}
          <div>
            <div className="label-tiny mb-2">How are you feeling?</div>
            <div className="flex flex-wrap gap-1.5">
              {DEFAULT_TAGS.map((t) => {
                const active = tags.includes(t.key);
                return (
                  <button
                    key={t.key}
                    onClick={() => toggleTag(t.key)}
                    className={`ff-chip ${active ? "is-active" : ""}`}
                    data-testid={`tag-${t.key}`}
                  >
                    <span>{t.emoji}</span>
                    {t.label}
                  </button>
                );
              })}
            </div>
          </div>

          {/* Plan */}
          <div>
            <div className="flex items-baseline justify-between mb-2 gap-2">
              <div className="label-tiny">Today’s plan</div>
              <div className="flex items-center gap-2">
                {hasDismissed && !editIdeas && (
                  <button
                    type="button"
                    onClick={doRestore}
                    className="label-tiny text-[color:var(--ink-3)] hover:text-[color:var(--ink)]"
                    data-testid="plan-restore"
                  >
                    Restore hidden
                  </button>
                )}
                <button
                  type="button"
                  onClick={() => setEditIdeas((v) => !v)}
                  className="label-tiny text-[color:var(--ink-3)] hover:text-[color:var(--ink)]"
                  data-testid="plan-edit-toggle"
                >
                  {editIdeas ? "Done" : "Hide ideas"}
                </button>
              </div>
            </div>
            <div className="flex flex-wrap gap-1.5 mb-2">
              {primaryPresets.map((p) => {
                const active = plans.includes(p);
                if (editIdeas) {
                  return (
                    <button
                      key={p}
                      onClick={() => doDismiss(p)}
                      className="ff-chip opacity-70 hover:opacity-100"
                      data-testid={`plan-dismiss-${p.replace(/\s+/g, "-").toLowerCase()}`}
                      title={`Hide "${p}" suggestion`}
                    >
                      {p} <span className="ml-1">×</span>
                    </button>
                  );
                }
                return (
                  <button
                    key={p}
                    onClick={() => togglePlan(p)}
                    className={`ff-chip ${active ? "is-active" : ""}`}
                    data-testid={`plan-preset-${p.replace(/\s+/g, "-").toLowerCase()}`}
                  >
                    {p}
                  </button>
                );
              })}
              {showAllPlans &&
                morePresets.map((p) => {
                  const active = plans.includes(p);
                  if (editIdeas) {
                    return (
                      <button
                        key={p}
                        onClick={() => doDismiss(p)}
                        className="ff-chip opacity-70 hover:opacity-100"
                        data-testid={`plan-dismiss-${p.replace(/\s+/g, "-").toLowerCase()}`}
                        title={`Hide "${p}" suggestion`}
                      >
                        {p} <span className="ml-1">×</span>
                      </button>
                    );
                  }
                  return (
                    <button
                      key={p}
                      onClick={() => togglePlan(p)}
                      className={`ff-chip ${active ? "is-active" : ""}`}
                      data-testid={`plan-preset-${p.replace(/\s+/g, "-").toLowerCase()}`}
                    >
                      {p}
                    </button>
                  );
                })}
              {/* Custom-added plans (not in presets) rendered as removable active chips */}
              {plans
                .filter(
                  (p) =>
                    !primaryPresets.includes(p) &&
                    !(showAllPlans && morePresets.includes(p)) &&
                    !morePresets.includes(p)
                )
                .map((p) => (
                  <button
                    key={p}
                    onClick={() => togglePlan(p)}
                    className="ff-chip is-active"
                    data-testid={`plan-custom-${p.replace(/\s+/g, "-").toLowerCase()}`}
                  >
                    {p} ×
                  </button>
                ))}
              {morePresets.length > 0 && (
                <button
                  type="button"
                  onClick={() => setShowAllPlans((s) => !s)}
                  className="ff-chip text-[color:var(--ink-2)]"
                  data-testid="plan-show-more"
                >
                  {showAllPlans ? "Show less" : `More (+${morePresets.length})`}
                </button>
              )}
            </div>
            <div className="flex gap-2">
              <input
                type="text"
                maxLength={80}
                value={customPlan}
                onChange={(e) => setCustomPlan(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    e.preventDefault();
                    addCustomPlan();
                  }
                }}
                placeholder="or type your own"
                className="ff-input flex-1"
                data-testid="checkin-plan"
              />
              <button
                type="button"
                onClick={addCustomPlan}
                disabled={!customPlan.trim()}
                className="ff-btn"
                data-testid="checkin-plan-add"
              >
                Add
              </button>
            </div>
          </div>

          {/* More */}
          <div>
            <button
              type="button"
              onClick={() => setShowMore((s) => !s)}
              className="label-tiny inline-flex items-center gap-1 hover:text-[color:var(--ink)] transition-colors"
              data-testid="checkin-show-more"
            >
              {showMore ? (
                <>
                  <ChevronUp className="w-3 h-3" strokeWidth={2} /> Less
                </>
              ) : (
                <>
                  <ChevronDown className="w-3 h-3" strokeWidth={2} /> More —
                  location, note
                </>
              )}
            </button>

            {showMore && (
              <div className="mt-4 space-y-5 pt-4 border-t border-[color:var(--line-soft)]">
                <div>
                  <div className="label-tiny mb-2">Where</div>
                  <div className="flex flex-wrap gap-1.5 mb-2">
                    {availableStatus.map((s) => {
                      const active = status === s.key;
                      return (
                        <button
                          key={s.key}
                          onClick={() => setStatus(s.key)}
                          className={`ff-chip ${active ? "is-active" : ""}`}
                          data-testid={`status-${s.key}`}
                        >
                          <span>{s.emoji}</span>
                          {s.label}
                        </button>
                      );
                    })}
                  </div>
                  <input
                    type="text"
                    maxLength={40}
                    value={customLocation}
                    onChange={(e) => setCustomLocation(e.target.value)}
                    placeholder="or a custom spot — ‘At Sofie’s’"
                    className="ff-input"
                    data-testid="checkin-custom-location"
                  />
                </div>

                <div>
                  <div className="flex items-center justify-between mb-2">
                    <div className="label-tiny">Note</div>
                    <button
                      type="button"
                      onClick={() => setNotePrivate((p) => !p)}
                      className={`label-tiny inline-flex items-center gap-1 hover:text-[color:var(--ink)] transition-colors`}
                      data-testid="checkin-note-private"
                      title={
                        notePrivate
                          ? hasPrivateNoteKey(auth.getFamilyId())
                            ? "End-to-end encrypted — only you (on this device) can read it"
                            : "Private — but the encryption key isn't on this device. Sign in with the family PIN to encrypt."
                          : "Shared — visible to the rest of your family"
                      }
                    >
                      {notePrivate ? (
                        <>
                          <EyeOff className="w-3 h-3" strokeWidth={2} /> Private · encrypted
                        </>
                      ) : (
                        <>
                          <Eye className="w-3 h-3" strokeWidth={2} /> Shared
                        </>
                      )}
                    </button>
                  </div>
                  <textarea
                    value={note}
                    onChange={(e) => setNote(e.target.value)}
                    rows={2}
                    maxLength={200}
                    placeholder="Anything you want to note…"
                    className="ff-input resize-none"
                    data-testid="checkin-note"
                  />
                </div>
              </div>
            )}
          </div>
        </div>

        <div className="sticky bottom-0 bg-[color:var(--surface)] border-t border-[color:var(--line-soft)] px-5 py-3 flex gap-2 justify-end">
          <button
            onClick={onClose}
            className="ff-btn"
            data-testid="checkin-cancel"
          >
            Cancel
          </button>
          <button
            onClick={handleSave}
            disabled={saving}
            className="ff-btn ff-btn-primary disabled:opacity-60"
            data-testid="checkin-save"
          >
            {saving ? "Saving…" : "Done"}
          </button>
        </div>
      </div>
    </div>
  );
}
